import React, { useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

export const PdfViewer = ({ url }) => {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [error, setError] = useState(null);

  const onLoad = useCallback(({ numPages: n }) => { setNumPages(n); setPageNumber(1); setError(null); }, []);
  const go = (d) => setPageNumber((p) => Math.min(numPages, Math.max(1, p + d)));

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="pdf-viewer">
      <div className="flex items-center justify-center gap-2 px-3 py-1.5 border-b bg-white text-sm">
        <Button size="sm" variant="ghost" onClick={() => go(-1)} disabled={pageNumber <= 1} data-testid="pdf-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
        <span className="flex items-center gap-1">
          Page
          <input type="number" min={1} max={numPages || 1} value={pageNumber}
            onChange={(e) => setPageNumber(Math.min(numPages || 1, Math.max(1, Number(e.target.value) || 1)))}
            className="w-14 border rounded px-1 py-0.5 text-center" data-testid="pdf-page-input" />
          <span data-testid="pdf-page-count">of {numPages || '…'}</span>
        </span>
        <Button size="sm" variant="ghost" onClick={() => go(1)} disabled={pageNumber >= numPages} data-testid="pdf-next-page"><ChevronRight className="h-4 w-4" /></Button>
        <span className="mx-2 h-4 w-px bg-slate-200" />
        <Button size="sm" variant="ghost" onClick={() => setScale((s) => Math.max(0.6, s - 0.2))} data-testid="pdf-zoom-out"><ZoomOut className="h-4 w-4" /></Button>
        <span className="w-12 text-center text-xs text-slate-500">{Math.round(scale * 100)}%</span>
        <Button size="sm" variant="ghost" onClick={() => setScale((s) => Math.min(3, s + 0.2))} data-testid="pdf-zoom-in"><ZoomIn className="h-4 w-4" /></Button>
      </div>
      <div className="flex-1 overflow-auto bg-slate-200/70 flex justify-center py-4">
        {error ? (
          <p className="text-sm text-rose-600 self-center px-6 text-center">Could not display this file here — use Download or open it in a new tab.</p>
        ) : (
          <Document file={url} onLoadSuccess={onLoad} onLoadError={(e) => setError(e)}
            loading={<Loader2 className="h-8 w-8 animate-spin text-emerald-600 mt-20" />}>
            <Page pageNumber={pageNumber} scale={scale} className="shadow-lg" renderAnnotationLayer renderTextLayer
              loading={<Loader2 className="h-6 w-6 animate-spin text-emerald-600 mt-10" />} />
          </Document>
        )}
      </div>
    </div>
  );
};
